import { useState } from "react";

interface FilterBarProps {
  genres: string[];
  years: number[];
  regions: string[];
  genre: string;
  year: string;
  region: string;
  sort: string;
  resultCount: number;
  onGenre: (value: string) => void;
  onYear: (value: string) => void;
  onRegion: (value: string) => void;
  onSort: (value: string) => void;
  onReset: () => void;
}

interface ChipRowProps {
  label: string;
  options: (string | number)[];
  value: string;
  allLabel: string;
  onSelect: (value: string) => void;
}

function ChipRow({ label, options, value, allLabel, onSelect }: ChipRowProps) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-start gap-2 sm:gap-4 py-3 border-b border-background-200 last:border-b-0">
      <span className="text-sm text-foreground-500 sm:w-16 shrink-0 pt-1.5">{label}</span>
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => onSelect("全部")}
          className={`px-3 py-1.5 rounded-md text-sm whitespace-nowrap cursor-pointer transition-colors ${
            value === "全部"
              ? "bg-primary-500 text-background-50"
              : "bg-background-100 text-foreground-600 hover:text-primary-600"
          }`}
        >
          {allLabel}
        </button>
        {options.map((option) => {
          const str = String(option);
          return (
            <button
              key={str}
              type="button"
              onClick={() => onSelect(str)}
              className={`px-3 py-1.5 rounded-md text-sm whitespace-nowrap cursor-pointer transition-colors ${
                value === str
                  ? "bg-primary-500 text-background-50"
                  : "bg-background-100 text-foreground-600 hover:text-primary-600"
              }`}
            >
              {str}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default function FilterBar({
  genres,
  years,
  regions,
  genre,
  year,
  region,
  sort,
  resultCount,
  onGenre,
  onYear,
  onRegion,
  onSort,
  onReset,
}: FilterBarProps) {
  const [panelOpen, setPanelOpen] = useState(false);

  const sortOptions = [
    { key: "updated", label: "按更新时间" },
    { key: "hot", label: "按热度" },
    { key: "rating", label: "按评分" },
  ];

  const activeFilters =
    (genre !== "全部" ? 1 : 0) + (year !== "全部" ? 1 : 0) + (region !== "全部" ? 1 : 0);

  return (
    <div className="rounded-lg bg-background-50 border border-background-200 overflow-hidden">
      <div className="flex items-center justify-between gap-3 p-4 border-b border-background-200">
        <div className="flex items-center gap-3 min-w-0">
          <div className="flex items-center gap-2 h-9 rounded-md bg-background-100 border border-background-300 p-1">
            {sortOptions.map((option) => (
              <button
                key={option.key}
                type="button"
                onClick={() => onSort(option.key)}
                className={`px-3 py-1 rounded-md text-xs md:text-sm whitespace-nowrap cursor-pointer transition-colors ${
                  sort === option.key
                    ? "bg-secondary-500 text-background-50"
                    : "text-foreground-600 hover:text-primary-600"
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
          <span className="hidden sm:block text-sm text-foreground-500 whitespace-nowrap">
            共 {resultCount} 部
          </span>
        </div>

        <button
          type="button"
          onClick={() => setPanelOpen((v) => !v)}
          className="flex items-center gap-1.5 px-3 py-2 rounded-md border border-background-300 text-sm text-foreground-700 cursor-pointer hover:border-primary-400 hover:text-primary-600 lg:hidden whitespace-nowrap"
        >
          <i className="ri-filter-3-line"></i>
          筛选
          {activeFilters > 0 && (
            <span className="w-4 h-4 flex items-center justify-center rounded-full bg-primary-500 text-background-50 text-[10px]">
              {activeFilters}
            </span>
          )}
        </button>

        <button
          type="button"
          onClick={onReset}
          className="hidden lg:flex items-center gap-1.5 px-3 py-2 rounded-md text-sm text-foreground-500 cursor-pointer hover:text-primary-600 whitespace-nowrap"
        >
          <i className="ri-refresh-line"></i>
          重置筛选
        </button>
      </div>

      <div className={`${panelOpen ? "block" : "hidden"} lg:block px-4 pb-2`}>
        <ChipRow
          label="类型"
          options={genres}
          value={genre}
          allLabel="全部类型"
          onSelect={onGenre}
        />
        <ChipRow
          label="年份"
          options={years}
          value={year}
          allLabel="全部年份"
          onSelect={onYear}
        />
        <ChipRow
          label="地区"
          options={regions}
          value={region}
          allLabel="全部地区"
          onSelect={onRegion}
        />
      </div>
    </div>
  );
}